import type { LogisticsCalculationContent, LogisticsCalculationCurrency } from './content.js';
import { LOGISTICS_CALCULATION_SECTIONS } from './content.js';
import type { LogisticsCalculationMoneyTotals, LogisticsCalculationOutput } from './calculation.js';
import {
  calculateLogisticsCalculationPayable,
  sumLogisticsCalculationMoney,
} from './calculation.js';
import type { LogisticsCalculationPublicDto } from './public.js';

type LineResult = LogisticsCalculationOutput['cargos'][number]['lines'][number];
type PublicCargoSections = NonNullable<LogisticsCalculationPublicDto['cargos'][number]['sections']>;

const CURRENCY_SIGNS = { USD: '$', CNY: '¥', RUB: '₽' } as const;
const CLEAN_DECIMAL = /^-?\d+(?:\.\d+)?$/;
const NBSP = '\u00a0';

const PAYMENT_LABELS = {
  novat: 'Без НДС',
  vat_included: 'С НДС',
  individual: 'Физическое лицо',
} as const;

const UNIT_LABELS = {
  kg: 'кг',
  m3: 'м³',
  package: 'место',
  item: 'шт.',
  cargo: 'груз',
  trip: 'рейс',
  day: 'день',
  fixed: 'фикс.',
} as const satisfies Record<
  Exclude<
    LogisticsCalculationContent['cargos'][number]['lines'][number]['unit'],
    'custom' | null | undefined
  >,
  string
>;

/**
 * Renders a money string the way the approved ТТН sheet prints it: `$` and `¥`
 * before the amount, `₽` after it, and kopecks only when a rouble amount has
 * them. Dollars and yuan always keep two decimals.
 *
 * WHY string arithmetic instead of `Intl.NumberFormat`: the amount arrives
 * already rounded, and a round trip through a float would round it again.
 */
export function formatSheetAmount(amount: string, currency: LogisticsCalculationCurrency): string {
  const negative = amount.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? amount.slice(1) : amount).split('.');
  const grouped = whole.replace(/\B(?=(?:\d{3})+(?!\d))/g, NBSP);
  const value =
    currency === 'RUB' && /^0*$/.test(fraction) ? grouped : `${grouped},${fraction.padEnd(2, '0')}`;
  const signed = `${negative ? '-' : ''}${value}`;
  // The dollar and yuan signs hug the figure, as the masthead rate already
  // prints them; only the rouble sign follows its figure with a space.
  return currency === 'RUB'
    ? `${signed}${NBSP}${CURRENCY_SIGNS.RUB}`
    : `${CURRENCY_SIGNS[currency]}${signed}`;
}

/** A Russian count noun: `1 груз`, `2 груза`, `5 грузов`, `21 груз`, `12 грузов`. */
export function pluralRu(count: number, forms: readonly [string, string, string]): string {
  const rem100 = Math.abs(count) % 100;
  const rem10 = rem100 % 10;
  if (rem100 >= 11 && rem100 <= 14) return forms[2];
  if (rem10 === 1) return forms[0];
  if (rem10 >= 2 && rem10 <= 4) return forms[1];
  return forms[2];
}

/**
 * Reads a registry value only when it is an unambiguous decimal.
 *
 * INVARIANT: dirty strings such as `по факту`, `5 $/kg + 200` or a spreadsheet
 * formula are never guessed at — they yield `null` so the caller can leave the
 * field to the manager instead of printing a fabricated number.
 */
export function parseDocumentDecimal(raw: string): number | null {
  return CLEAN_DECIMAL.test(raw) ? Number.parseFloat(raw) : null;
}

function amountDisplay(
  amount: string,
  currency: 'USD' | 'CNY' | 'RUB' | null,
  caption: string | null,
): string {
  if (currency === null) return '';
  if (caption) return caption;
  return `${amount} ${CURRENCY_SIGNS[currency]}`;
}

/**
 * Per-currency sum of the amounts as each line states them, before conversion.
 *
 * WHY not `calculation.sections[…].totals`: a converted CNY line contributes
 * only to USD there, so the yuan half of a group subtotal exists nowhere in the
 * frozen output and has to be projected separately.
 */
function faceTotals(results: readonly LineResult[]): LogisticsCalculationMoneyTotals {
  const sumOf = (currency: LogisticsCalculationCurrency): string =>
    sumLogisticsCalculationMoney(
      results.filter((result) => result.currency === currency).map((result) => result.amount),
    );
  return { usd: sumOf('USD'), cny: sumOf('CNY'), rub: sumOf('RUB') };
}

function cargoSections(
  lines: LogisticsCalculationContent['cargos'][number]['lines'],
  result: LogisticsCalculationOutput['cargos'][number],
): PublicCargoSections {
  return Object.fromEntries(
    LOGISTICS_CALCULATION_SECTIONS.map((section) => [
      section,
      {
        ...result.sections[section],
        faceTotals: faceTotals(
          result.lines.filter((_r, index) => lines[index]!.section === section),
        ),
      },
    ]),
  ) as PublicCargoSections;
}

function lineAmounts(rate: string, result: LineResult) {
  return {
    amount: result.amount,
    rate,
    currency: result.currency,
    convertedAmountUsd: result.convertedAmountUsd,
  };
}

function unitPriceDisplay(
  rate: string,
  currency: 'USD' | 'CNY' | 'RUB' | null | undefined,
): string {
  if (!rate || currency === null || currency === undefined) return '';
  return `${rate} ${currency}`;
}

function unitDisplay(
  line: Pick<LogisticsCalculationContent['cargos'][number]['lines'][number], 'unit' | 'customUnit'>,
): string {
  if (line.unit === null || line.unit === undefined) return '';
  if (line.unit === 'custom') return line.customUnit ?? line.unit;
  return UNIT_LABELS[line.unit];
}

/**
 * The typed cost cells, passed through exactly as the manager entered them.
 *
 * An absent cost stays absent: the document prints an em dash for it, and the
 * DTO must not gain a key the stored cargo does not carry.
 */
function cargoCosts(
  cargo: LogisticsCalculationContent['cargos'][number],
): Pick<
  LogisticsCalculationPublicDto['cargos'][number],
  'transport' | 'insurance' | 'packaging' | 'extras' | 'prr' | 'clientDelivery'
> {
  return {
    ...(cargo.transport === undefined ? {} : { transport: cargo.transport }),
    ...(cargo.insurance === undefined ? {} : { insurance: cargo.insurance }),
    ...(cargo.packaging === undefined ? {} : { packaging: cargo.packaging }),
    ...(cargo.extras === undefined ? {} : { extras: cargo.extras }),
    ...(cargo.prr === undefined ? {} : { prr: cargo.prr }),
    ...(cargo.clientDelivery === undefined ? {} : { clientDelivery: cargo.clientDelivery }),
  };
}

/** Pure confidentiality boundary shared by saved preview, public page, and PDF. */
export function buildLogisticsCalculationSheetModel(
  content: LogisticsCalculationContent,
  calculation: LogisticsCalculationOutput,
  options: { publicCode: string },
): LogisticsCalculationPublicDto {
  return {
    style: content.style,
    ...(content.layoutVersion === 'v2' ? { layoutVersion: content.layoutVersion } : {}),
    publicCode: options.publicCode,
    documentDate: content.documentDate,
    payDueDate: content.payDueDate,
    client: {
      company: content.clientCompany,
      contactName: content.clientContactName,
      contactPhone: content.clientContactPhone,
    },
    manager: {
      name: content.managerName,
      phone: content.managerPhone,
      telegram: content.managerTelegram ?? null,
      note: content.managerNote ?? null,
    },
    paymentLabel: content.paymentLabel,
    paymentLabelDisplay: PAYMENT_LABELS[content.paymentLabel],
    ...(content.cnyPerUsdRate === undefined ? {} : { cnyPerUsdRate: content.cnyPerUsdRate }),
    rubPerUsdRate: content.rubPerUsdRate,
    ...(content.rubPerCnyRate === undefined ? {} : { rubPerCnyRate: content.rubPerCnyRate }),
    cargos: content.cargos.map((cargo, cargoIndex) => ({
      article: cargo.article,
      name: cargo.name,
      seats: cargo.seats,
      weight: cargo.weight,
      volume: cargo.volume,
      pricingBasis: cargo.pricingBasis ?? null,
      transportType: cargo.transportType ?? null,
      departedAt: cargo.departedAt ?? null,
      arrivedAt: cargo.arrivedAt ?? null,
      ...cargoCosts(cargo),
      lines: cargo.lines.map((line, lineIndex) => {
        const result = calculation.cargos[cargoIndex]!.lines[lineIndex]!;
        return {
          section: line.section,
          serviceName: line.serviceName,
          quantity: line.quantity,
          unit: unitDisplay(line),
          // DANGER: `unitPriceDisplay` stays v2-only. Both server PDF renderers
          // switch a four-column row to a five-column one on its presence, so
          // emitting it for `v1` would relayout already-published documents.
          // The document reads the raw `rate`/`currency` below instead.
          ...(content.layoutVersion === 'v2'
            ? { unitPriceDisplay: unitPriceDisplay(line.rate, line.currency) }
            : {}),
          ...lineAmounts(line.rate, result),
          amountDisplay: amountDisplay(result.amount, result.currency, result.conversionCaption),
        };
      }),
      sections: cargoSections(cargo.lines, calculation.cargos[cargoIndex]!),
      totals: calculation.cargos[cargoIndex]!.totals,
    })),
    commonServices: content.commonServices.map((line, lineIndex) => {
      const result = calculation.commonServices.lines[lineIndex]!;
      return {
        serviceName: line.serviceName,
        quantity: line.quantity,
        unit: unitDisplay(line),
        ...(content.layoutVersion === 'v2'
          ? { unitPriceDisplay: unitPriceDisplay(line.rate, line.currency) }
          : {}),
        ...lineAmounts(line.rate, result),
        amountDisplay: amountDisplay(result.amount, result.currency, result.conversionCaption),
      };
    }),
    commonServicesTotals: calculation.commonServices.totals,
    commonServicesFaceTotals: faceTotals(calculation.commonServices.lines),
    totals: calculation.totals,
    grandTotalUsd: calculation.totals.usd,
    grandTotalRub: calculation.totals.rub,
    // Derived here rather than stored: the frozen totals, rates and divisor a
    // published snapshot already carries are everything the block needs. The
    // divisor rides along verbatim so the document can print the formula the
    // manager typed, not one reconstructed from the two rounded figures.
    payable: {
      ...calculateLogisticsCalculationPayable(calculation.totals, content, content.paymentDivisor),
      ...(content.paymentDivisor === undefined ? {} : { divisor: content.paymentDivisor }),
      ...(content.payableCurrency === undefined ? {} : { currency: content.payableCurrency }),
    },
    allRubReference: calculation.allRubReference,
  };
}
